import { useCallback } from 'react';

import { CloseIcon } from '@braskai/icons';
import { Button, Modal, Typography } from '@braskai/ui-kit';
import { useNavigate } from 'react-router-dom';

import { Stack } from '@mui/material';
import { Box } from '@mui/system';

import { useCurrentUser } from '@/entities/user/currentUser';

import { FaceSadIcon } from '@/shared/assets/icons/FaceSadIcon';
import { FeaturedDowngradeIcon } from '@/shared/assets/icons/FeaturedDowngradeIcon';
import { PATHS } from '@/shared/constants/routes/paths';
import { useInterfaceLanguage } from '@/shared/i18n';
import { formatInterfaceDate } from '@/shared/i18n/formatLocale';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval } from '@/shared/types/Billing';

import { usePlanChangeConfirmation } from '../../../model/usePlanChangeConfirmation';
import { useDowngradeConfirmationModalStore } from '../../model/useDowngradeConfirmationModalStore';
import {
  SActions,
  SCloseButton,
  SContent,
  SHeader,
  SHeaderTop,
  SModalContainer,
  SNewPlanSection,
  SPlanSection,
} from './styled';

export const DowngradeConfirmationModal = () => {
  const isOpen = useDowngradeConfirmationModalStore((state) => state.isOpen);
  const close = useDowngradeConfirmationModalStore((state) => state.close);
  const currentPlan = useDowngradeConfirmationModalStore((state) => state.currentPlan);
  const targetPlan = useDowngradeConfirmationModalStore((state) => state.targetPlan);
  const targetPrice = useDowngradeConfirmationModalStore((state) => state.targetPrice);
  const featuresToLose = useDowngradeConfirmationModalStore((state) => state.featuresToLose);

  const { data: user } = useCurrentUser();
  const { locale, t } = useInterfaceLanguage();
  const navigate = useNavigate();
  const handleClosedAfterProcessing = useCallback(() => navigate(PATHS.homepage), [navigate]);

  const openPlanChangeConfirmation = usePlanChangeConfirmation({
    onClosedAfterProcessing: handleClosedAfterProcessing,
  });

  const handleDowngrade = () => {
    if (!targetPrice?.id || !targetPlan?.id) return;

    MixpanelEvents.scheduleDowngradeClick({
      currentPlan: currentPlan?.name ?? '',
      newPlan: targetPlan.name,
    });
    close();

    openPlanChangeConfirmation({
      confirmButtonText: t('pricing.change.scheduleDowngrade'),
      errorMessage: t('pricing.change.downgradeError'),
      planId: targetPlan.id,
      priceId: targetPrice.id,
      successDescriptions: {
        noPayment: t('pricing.change.downgradeSuccess'),
        paid: t('pricing.change.changeSuccess'),
      },
      onConfirmAnalytics: () =>
        MixpanelEvents.scheduleDowngradeConfirmClick({
          currentPlan: currentPlan?.name ?? '',
          newPlan: targetPlan.name,
        }),
    });
  };

  const handleCancel = () => {
    close();
  };

  if (!currentPlan || !targetPlan || !targetPrice) {
    return null;
  }

  const currentIntervalLabel = t(
    user?.subscription?.plan.interval === PaymentInterval.Year ? 'pricing.change.yearly' : 'pricing.change.monthly',
  );
  const targetIntervalLabel = t(
    targetPrice.recurring?.interval === PaymentInterval.Year ? 'pricing.change.yearly' : 'pricing.change.monthly',
  );

  return (
    <Modal open={isOpen} onClose={handleCancel}>
      <SModalContainer>
        {/* Header */}
        <SHeader>
          <SHeaderTop>
            <FeaturedDowngradeIcon color="error" height={56} width={56} />
            <SCloseButton data-testid="downgrade-confirmation-modal-cancel" onClick={handleCancel}>
              <CloseIcon height={24} width={24} />
            </SCloseButton>
          </SHeaderTop>
        </SHeader>

        {/* Content */}
        <SContent>
          {/* Current Plan */}
          <SPlanSection>
            <Typography color="grey.900" variant="body-1-md">
              {t('pricing.change.current')}
            </Typography>
            {user?.subscription?.current_period_end && (
              <Typography color="grey.700" variant="body-2">
                {t('pricing.change.currentDescription', {
                  date: formatInterfaceDate(locale, user.subscription.current_period_end),
                  interval: currentIntervalLabel,
                  plan: currentPlan.name,
                })}
              </Typography>
            )}
          </SPlanSection>

          {/* New Plan */}
          <SNewPlanSection>
            <SPlanSection>
              <Typography color="error.700" variant="body-1-md">
                {t('pricing.change.new')}
              </Typography>
              <Typography color="grey.700" variant="body-2">
                {t('pricing.change.downgradeDescription', {
                  interval: targetIntervalLabel,
                  plan: targetPlan.name,
                })}
              </Typography>
            </SPlanSection>

            {/* Features You Lose */}
            {featuresToLose && featuresToLose.length > 0 && (
              <Stack
                alignItems="flex-start"
                border="1px solid"
                borderColor="grey.200"
                borderRadius={12}
                direction="row"
                gap="10px"
                p={16}
                sx={{ backgroundColor: 'grey.50' }}
              >
                <Box>
                  <FaceSadIcon height={20} width={20} />
                </Box>
                <Stack gap="2px">
                  <Typography color="grey.900" variant="body-2-md">
                    {t('pricing.change.featuresLost')}
                  </Typography>
                  <Typography color="grey.700" variant="body-2">
                    {featuresToLose.join(', ')}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </SNewPlanSection>
        </SContent>

        {/* Actions */}
        <SActions>
          <Button
            data-testid="downgrade-confirmation-modal-schedule-downgrade"
            size="lg"
            variant="error"
            fullWidth
            onClick={handleDowngrade}
          >
            {t('pricing.change.scheduleDowngrade')}
          </Button>
          {user?.subscription?.current_period_end && (
            <Typography color="grey.500" sx={{ textAlign: 'center' }} variant="caption-1">
              {t('pricing.change.activeUntil', {
                date: formatInterfaceDate(locale, user.subscription.current_period_end),
              })}
            </Typography>
          )}
        </SActions>
      </SModalContainer>
    </Modal>
  );
};
